import React, { useEffect, useMemo, useState } from 'react';
import { Msg, SubTabs, panelGet, useAdminGet } from './kit';
import { type AdminTargetIn } from './adminNav';
import Overview from './ipintel/Overview';
import Orders from './ipintel/Orders';
import OrderDrawer from './ipintel/OrderDrawer';
import Leads from './ipintel/Leads';
import BulkEnquiries from './ipintel/BulkEnquiries';
import Registrations from './ipintel/Registrations';
import Activity from './ipintel/Activity';
import Pricing from './ipintel/Pricing';
import Coupons from './ipintel/Coupons';
import Samples, { normSamples } from './ipintel/Samples';
import EditableTemplates from './email/EditableTemplates';
import { S, num } from './ipintel/shared';

/**
 * /admin → IP Intel Report: the paid diligence report sold from
 * /ip-intel-report. Orders (payment → intake → delivery), the sample-report
 * leads, the bulk-pricing enquiries, the IIT Bombay × IRCC register-interest
 * registrations (Backend/ip_intel_programme.py), the page's click stream, the per-region
 * price, the sample reports (text, cover, PDF — editable) and the IP Intel
 * e-mails (the shared template editor, narrowed to this group).
 * Backed by Backend/ip_intel.py (panel section "ip_intel") plus raw admin
 * routes for the Excel export, file downloads and the uploads (paths in
 * ipintel/shared.tsx).
 */

const TABS = ['Overview', 'Orders', 'Sample leads', 'Bulk enquiries', 'IRCC registrations', 'Activity', 'Pricing', 'Coupons', 'Samples', 'Emails'];
/** email_templates group of the IP Intel mails; the key prefix also catches one the group misses. */
const EMAIL_GROUP = 'IP Intel Report';

/** A deep link's tab=: the label itself or its snake_case ("Sample leads" → sample_leads). */
const tabKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const tabFromLink = (raw: string) => TABS.find(t => t === raw || tabKey(t) === tabKey(raw));

/**
 * Deep link (owner, 2026-10-05): /admin?group=ip_intel&tab=orders[&ref=<order ref>]
 * — the ref is resolved with ip_intel/order?ref= and that order's drawer opens
 * over the Orders tab.
 */
const IpIntelPanel: React.FC<{ initialTab?: string; target?: AdminTargetIn }> = ({ initialTab, target }) => {
  const [tab, setTab] = useState(initialTab && TABS.includes(initialTab) ? initialTab : 'Overview');
  const [ordersPreset, setOrdersPreset] = useState<{ status: string; nonce: number } | null>(null);
  const [bulkPreset, setBulkPreset] = useState<{ status: string; nonce: number } | null>(null);
  const [linkedOrder, setLinkedOrder] = useState<string | null>(null);
  const [linkErr, setLinkErr] = useState('');
  // Bumped when the linked order's drawer changes something, so the list behind it refetches.
  const [ordersKey, setOrdersKey] = useState(0);

  useEffect(() => {
    if (initialTab && TABS.includes(initialTab)) setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!target) return;
    const ref = String(target.ref || '').trim();
    const t = tabFromLink(String(target.tab || '').trim()) || (ref ? 'Orders' : undefined);
    if (t) { setOrdersPreset(null); setBulkPreset(null); setTab(t); }
    setLinkedOrder(null);
    setLinkErr('');
    if (!ref) return;
    let live = true;
    panelGet(S, 'order', { ref }).then(({ data, error }) => {
      if (!live) return;
      const id = data?.order?.id ? String(data.order.id) : '';
      if (id) setLinkedOrder(id);
      else setLinkErr(`Could not open order ${ref}: ${error || 'not found'}`);
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);

  // Samples feed both the Samples tab and the Leads report filter.
  const samplesRes = useAdminGet(S, 'samples', {}, []);
  const samples = useMemo(() => normSamples(samplesRes.data), [samplesRes.data]);
  const turnaround = num(samplesRes.data?.turnaround_days) || 7;

  const openOrders = (status: string) => {
    setOrdersPreset({ status, nonce: Date.now() });
    setTab('Orders');
  };
  const openBulk = (status: string) => {
    setBulkPreset({ status, nonce: Date.now() });
    setTab('Bulk enquiries');
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* A tab click is a fresh start: the filter a KPI tile handed over
            applies to that one jump, not to every later visit. */}
        <SubTabs tabs={TABS} active={tab}
          onChange={t => { setOrdersPreset(null); setBulkPreset(null); setLinkErr(''); setTab(t); }} />
        <a href="/ip-intel-report" target="_blank" rel="noopener noreferrer"
          className="text-xs text-primary-700 underline">Open the public page ↗</a>
      </div>
      {linkErr && <Msg kind="error">{linkErr}</Msg>}
      {tab === 'Overview' && <Overview onOpenOrders={openOrders} onOpenBulk={openBulk} />}
      {tab === 'Orders' && <Orders key={ordersKey} preset={ordersPreset} turnaroundDays={turnaround} />}
      {tab === 'Sample leads' && <Leads samples={samples} />}
      {tab === 'Bulk enquiries' && <BulkEnquiries preset={bulkPreset} />}
      {tab === 'IRCC registrations' && <Registrations />}
      {tab === 'Activity' && <Activity />}
      {tab === 'Pricing' && <Pricing />}
      {tab === 'Coupons' && <Coupons />}
      {tab === 'Samples' && (
        <Samples samples={samples} loading={samplesRes.loading} error={samplesRes.error} reload={samplesRes.reload} />
      )}
      {tab === 'Emails' && (
        <EditableTemplates groups={[EMAIL_GROUP]} keyPrefix="ip_intel_"
          title="IP Intel Report e-mails — edit the wording, no deploy needed" />
      )}
      {linkedOrder && (
        <OrderDrawer orderId={linkedOrder} turnaroundDays={turnaround}
          onClose={() => setLinkedOrder(null)}
          onChanged={() => setOrdersKey(k => k + 1)} />
      )}
    </div>
  );
};

export default IpIntelPanel;
